import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { contentStringApi, contentStringKeys } from '@/api/skin/contentStringApi';
import { AppType } from '@/models/enums/AppType';
import { appTypeLabel } from '@/models/media-network/appLabels';
import {
    type ContentStringCategory, type ContentStringKey, FALLBACK_LOCALE,
    parseLocaleValues, placeholderToken, toStringJson,
} from '@/models/skin/ContentString';
import { useLocalizedStrings } from '../../../localization/LocalizedStringsProvider';
import LocaleValueEditor from '../../common/LocaleValueEditor';
import MultiSelectCheck from '../../common/MultiSelectCheck';
import { useToast } from '../../common/toast/ToastProvider';
import ReadOnlyFieldset from '../../common/dialog/ReadOnlyFieldset';

interface Props {
    open: boolean;
    /** Null when creating. */
    editing: ContentStringKey | null;
    categories: ContentStringCategory[];
    // View-only: the grid opens this with an eye rather than a pencil for read access alone.
    readOnly?: boolean;
    onClose: () => void;
}

const ALL_APP_TYPES = Object.values(AppType) as number[];

const ContentStringKeyDialog = ({ open, editing, categories, readOnly, onClose }: Props) => {
    const strings = useLocalizedStrings();
    const toast = useToast();
    const qc = useQueryClient();

    const [key, setKey] = useState(editing?.key ?? '');
    const [categoryId, setCategoryId] = useState(editing?.categoryId ?? categories[0]?.id ?? '');
    const [appTypes, setAppTypes] = useState<number[]>(editing?.appTypes ?? []);
    const [placeholders, setPlaceholders] = useState<string[]>(editing?.placeholders ?? []);
    const [newPlaceholder, setNewPlaceholder] = useState('');

    // Defaults are authored per language. en-US is always present because it is the chain's last resort - a
    // key without it can only ever resolve to its own name.
    const [values, setValues] = useState<Record<string, string>>(() => {
        const parsed = parseLocaleValues(editing?.defaultStringJson);
        const initial: Record<string, string> = { [FALLBACK_LOCALE]: '' };
        for (const [locale, value] of Object.entries(parsed)) initial[locale] = value;
        if (parsed[FALLBACK_LOCALE.toLowerCase()]) initial[FALLBACK_LOCALE] = parsed[FALLBACK_LOCALE.toLowerCase()];
        return initial;
    });

    const [newLocale, setNewLocale] = useState('');

    const locales = useMemo(
        () => Object.keys(values).sort((a, b) => (a === FALLBACK_LOCALE ? -1 : b === FALLBACK_LOCALE ? 1 : a.localeCompare(b))),
        [values],
    );

    const trimmedKey = key.trim();
    const hasEnglish = (values[FALLBACK_LOCALE] ?? '').trim().length > 0;
    const canSave = trimmedKey.length > 0 && !!categoryId && appTypes.length > 0;

    const { mutateAsync: save, isPending: saving } = useMutation({
        mutationFn: async () => {
            const json = toStringJson(values);
            const body = {
                key: trimmedKey,
                categoryId,
                // An empty array is not "no default" - send null so the server stores nothing at all.
                defaultStringJson: JSON.parse(json).length === 0 ? null : json,
                appTypes,
                placeholders,
            };

            if (editing) await contentStringApi.updateKey(editing.id, body);
            else await contentStringApi.createKey(body);
        },
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.contentStringKeySaved });
            await qc.invalidateQueries({ queryKey: contentStringKeys.keys() });
            onClose();
        },
        onError: (e: unknown) => {
            // The server refuses a duplicate name with a 409 and its own wording; showing that beats a generic
            // failure, since "a key named X already exists" tells the admin exactly what to change.
            const message = (e as { body?: string })?.body ?? strings.contentStringKeySaveError;
            toast({ severity: 'error', message: typeof message === 'string' && message ? message : strings.contentStringKeySaveError });
        },
    });

    /**
     * Names are lowercased and stripped to a bare identifier here as well as on the server.
     *
     * Not belt-and-braces: substitution is a literal replace, so "#Points#" would never match "#points#", and
     * an admin who typed it that way would get a placeholder that silently never fires. Fixing it at the
     * point of entry is the difference between a rejected save and a wrong one.
     */
    const addPlaceholder = () => {
        const name = newPlaceholder.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
        if (!name) return;
        if (placeholders.includes(name)) return;
        setPlaceholders(p => [...p, name]);
        setNewPlaceholder('');
    };

    const addLocale = () => {
        const locale = newLocale.trim();
        if (!locale) return;
        if (Object.keys(values).some(l => l.toLowerCase() === locale.toLowerCase())) return;
        setValues(v => ({ ...v, [locale]: '' }));
        setNewLocale('');
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{editing ? strings.contentStringEditKey : strings.contentStringNewKey}</DialogTitle>

            <DialogContent>
                <ReadOnlyFieldset readOnly={readOnly}>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        fullWidth
                        label={strings.contentStringKey}
                        value={key}
                        onChange={e => setKey(e.target.value)}
                        // Renaming is safe: brand overrides reference the key's id, not its name.
                        helperText={strings.contentStringKeyHelp}
                    />

                    <TextField
                        select
                        fullWidth
                        label={strings.category}
                        value={categoryId}
                        onChange={e => setCategoryId(e.target.value)}
                    >
                        {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
                    </TextField>

                    <FormControl fullWidth error={appTypes.length === 0}>
                        <InputLabel id="apps-label">{strings.contentStringApps}</InputLabel>
                        <MultiSelectCheck<number>
                            labelId="apps-label"
                            id="apps"
                            label={strings.contentStringApps}
                            value={appTypes}
                            onChange={setAppTypes}
                            options={ALL_APP_TYPES.map(a => ({ value: a, label: appTypeLabel(a as AppType) }))}
                        />
                        <FormHelperText>{appTypes.length === 0 ? strings.contentStringAppsRequired : ' '}</FormHelperText>
                    </FormControl>

                    {/* Declared here rather than inferred from the default text, because what an app CAN
                        substitute is a platform fact. Reading it back out of whatever wording happens to be
                        in the default would make a typo in that text silently redefine the contract. */}
                    <Box>
                        <Typography variant="subtitle2">{strings.contentStringPlaceholders}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {strings.contentStringPlaceholdersHelp}
                        </Typography>

                        <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                            {placeholders.map(name => (
                                <Chip
                                    key={name}
                                    size="small"
                                    label={placeholderToken(name)}
                                    onDelete={() => setPlaceholders(p => p.filter(x => x !== name))}
                                />
                            ))}
                            {placeholders.length === 0 && (
                                <Typography variant="caption" color="text.secondary">
                                    {strings.contentStringNoPlaceholders}
                                </Typography>
                            )}
                        </Stack>

                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <TextField
                                size="small"
                                label={strings.contentStringAddPlaceholder}
                                placeholder="points"
                                value={newPlaceholder}
                                onChange={e => setNewPlaceholder(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPlaceholder(); } }}
                            />
                            <Button startIcon={<AddIcon />} onClick={addPlaceholder} disabled={!newPlaceholder.trim()}>
                                {strings.add}
                            </Button>
                        </Stack>
                    </Box>

                    <Divider />

                    <Box>
                        <Typography variant="subtitle2">{strings.contentStringDefaults}</Typography>
                        <Typography variant="caption" color="text.secondary">
                            {strings.contentStringDefaultsHelp}
                        </Typography>
                    </Box>

                    {/* One field at a time, tab per language. A field per language does not survive a catalogue
                        authored in thirty markets - the dialog becomes a scroll hunt, and finding which
                        languages are still empty means reading all thirty. The dot on a tab marks one that
                        has text. */}
                    <LocaleValueEditor
                        locales={locales}
                        values={values}
                        onChange={(locale, value) => setValues(v => ({ ...v, [locale]: value }))}
                        removeLabel={strings.remove}
                        // en-US is never removable: without it the key resolves to its own name for every
                        // brand in every language.
                        canRemoveLocale={locale => locale !== FALLBACK_LOCALE}
                        onRemoveLocale={locale => setValues(v => {
                            const next = { ...v };
                            delete next[locale];
                            return next;
                        })}
                        placeholders={placeholders}
                        placeholderLabel={strings.contentStringInsert}
                        unknownPlaceholderText={strings.contentStringUnknownPlaceholder}
                    />

                    {!hasEnglish && (
                        <Alert severity="error" variant="outlined">
                            {strings.contentStringNoEnglishWarning}
                        </Alert>
                    )}

                    <Stack direction="row" spacing={1}>
                        <TextField
                            size="small"
                            label={strings.contentStringAddLanguage}
                            placeholder="fr-CA"
                            value={newLocale}
                            onChange={e => setNewLocale(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLocale(); } }}
                        />
                        <Button startIcon={<AddIcon />} onClick={addLocale} disabled={!newLocale.trim()}>
                            {strings.add}
                        </Button>
                    </Stack>
                </Stack>
            </ReadOnlyFieldset>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={saving}>{readOnly ? strings.close : strings.cancel}</Button>
                {!readOnly && <Button variant="contained" onClick={() => save()} disabled={!canSave || saving} loading={saving}>
                    {strings.save}
                </Button>}
            </DialogActions>
        </Dialog>
    );
};

export default ContentStringKeyDialog;
